import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCatalogQuery, useWorkspaceRecordsQuery } from '@platform/state/api';
import { useLabel } from '@platform/hooks/useLabel';
import { todayIso } from '@platform/core/lib/format';
import { moduleNav, navGroups } from '@platform/navigation';
import { mainRecordTypes } from '../../shell/mainRecordTypes';
import { moduleCount } from '../../shell/moduleCount';
import { biStats } from './biStats';
import type { IBiStats } from './IBiStats';

/**
 * Зведення бізнес-аналітики робочого простору або групи модулів (`modules` — ключі; без них — усі модулі меню груп, у порядку груп верхнього
 * меню, як на «Усіх модулях»): лічильники каталогу
 * й записи головних таблиць, у яких є записи (один запит на тип, кешується); доки записи вантажаться — undefined.
 */
export function useBiStats(ws: string, modules?: readonly string[]): IBiStats | undefined {
  const { t } = useTranslation();
  const label = useLabel();
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const list = useMemo(() => {
    const order = [...new Set(navGroups().flatMap((g) => [...g.modules, ...g.subgroups.flatMap((s) => s.modules)]).map((k) => k.split('/')[0]!))];
    // власні модулі простору (ADR-025) — після модулів каталогу, у порядку каталогу (як ws.modules прототипу)
    const at = (key: string) => (order.includes(key) ? order.indexOf(key) : order.length);
    return (catalog?.modules ?? []).filter((m) => m.installed && moduleNav(m.key).listed && (!modules || modules.includes(m.key)))
      .sort((a, b) => at(a.key) - at(b.key));
  }, [catalog, modules]);
  const { data } = useWorkspaceRecordsQuery({ ws, types: mainRecordTypes(catalog, list.map((m) => m.key)) }, { skip: !catalog });
  return useMemo(() => (data
    ? biStats(data.items, list.map((m) => ({ key: m.key, name: label(m.name), total: moduleCount(m) ?? 0 })), Date.now(), todayIso(), t('biPage.unassigned'))
    : undefined), [data, list, label, t]);
}
